import { useEffect, useState } from 'react';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { tzDateKey } from '../../lib/format';

/** Toshkent "HH:MM" — brauzer qaysi mintaqada bo'lishidan qat'i nazar. */
export const soatTZ = (iso: string | Date) =>
  new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tashkent' });

/** Ketish belgilanmagan yozuv — `id` va kelish vaqti. */
export interface OchiqYozuv { id: string; checkIn: string }

/**
 * KETGAN VAQTINI QO'LDA KIRITISH (2026-11-01, maktab so'rovi): FACE ID kelishni yozgan, ketishni
 * yozmagan kunlar ko'p — panelda faqat "Keldi" turardi. Kun o'sha yozuvning Toshkent kuni,
 * admin faqat soatni tanlaydi; server (`PATCH /payroll/logs/:id`) kelishdan keyin ekanini,
 * kesishishni va kelajakni tekshiradi.
 */
export function KetishModal({ yozuv, onClose, sukut }: {
  yozuv: OchiqYozuv | null;
  onClose: () => void;
  /** Sukut soat "HH:MM" (masalan smena oxiri); berilmasa — bugun bo'lsa hozir, aks holda 17:00. */
  sukut?: string | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [vaqt, setVaqt] = useState('');
  const kun = yozuv ? tzDateKey(yozuv.checkIn) : '';
  useEffect(() => {
    if (!yozuv) return;
    const bugun = tzDateKey(new Date()) === kun;
    setVaqt(sukut || (bugun ? soatTZ(new Date()) : '17:00'));
  }, [yozuv, kun, sukut]);

  const ketish = /^\d{2}:\d{2}$/.test(vaqt) && kun ? new Date(`${kun}T${vaqt}:00+05:00`) : null;
  const kechmi = !!ketish && !!yozuv && ketish.getTime() <= new Date(yozuv.checkIn).getTime();

  const saqla = useMutation({
    mutationFn: () => api.patch(`/payroll/logs/${yozuv!.id}`, { checkOut: ketish!.toISOString() }),
    onSuccess: () => {
      // Ketish maoshga va davomatga ta'sir qiladi — karta, ro'yxatlar va davomat sahifasi
      for (const key of ['logs', 'open-log', 'salary-compute', 'salary-oylar', 'payroll-summary', 'payroll-oylik', 'staff-attendance']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
      notifications.show({ color: 'green', message: t('kunlar.outSaved') });
      onClose();
    },
    onError: (e) => notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
    }),
  });

  return (
    <Modal opened={!!yozuv} onClose={onClose} title={t('kunlar.outTitle')} size="sm">
      {yozuv && (
        <Stack>
          <div>
            <Text size="sm">{t('kunlar.outDay', { day: `${kun.slice(8, 10)}.${kun.slice(5, 7)}.${kun.slice(0, 4)}` })}</Text>
            <Text size="sm" c="dimmed">{t('kunlar.outCame', { time: soatTZ(yozuv.checkIn) })}</Text>
          </div>
          <TimeInput
            label={t('kunlar.outTime')}
            value={vaqt}
            onChange={(e) => setVaqt(e.currentTarget.value)}
            error={kechmi ? t('kunlar.outAfterIn') : undefined}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button onClick={() => saqla.mutate()} disabled={!ketish || kechmi} loading={saqla.isPending}>{t('kunlar.outSave')}</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
